import { Injectable, computed, signal, effect, inject, Renderer2, RendererFactory2 } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { ColorMode } from '../Types/color-mode.type';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly rendererFactory = inject(RendererFactory2);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly renderer: Renderer2;
  private readonly THEME_KEY = 'preferred_theme';

  readonly mode = signal<ColorMode>(this.resolveInitialMode());

  private readonly _chosen = signal<ColorMode | null>(this.readStoredMode());

  readonly chosenMode = computed<ColorMode | null>(() => this._chosen());

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);

    effect(() => {
      this.applyThemeToDom(this.mode());
    });
  }

  toggleMode(): void {
    const newMode = this.mode() === 'light' ? 'dark' : 'light';
    this.mode.set(newMode);
    this._chosen.set(newMode);
    this.localStorageService.setItem(this.THEME_KEY, newMode);
  }

  private resolveInitialMode(): ColorMode {
    return this.readStoredMode() ?? 'dark';
  }

  private readStoredMode(): ColorMode | null {
    const stored = this.localStorageService.getItem(this.THEME_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  }

  private applyThemeToDom(mode: ColorMode): void {
    const previousMode = mode === 'light' ? 'dark-theme' : 'light-theme';
    const currentMode = `${mode}-theme`;

    // index.html boots the theme class on <html>; left stale, its --bs-* leak into body.
    for (const element of [document.documentElement, document.body]) {
      this.renderer.removeClass(element, previousMode);
      this.renderer.addClass(element, currentMode);
    }
  }
}
